import React, { useState, useRef, useEffect } from 'react';
import { intelligentSearchService } from '../../services/IntelligentSearchService';

const LocationAutocomplete = ({ 
  placeholder = "Enter location to search...", 
  onSelect,
  className = "",
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const fetchPredictions = async (input) => {
    if (input.length < 2) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    setLoading(true);
    const { data, error } = await intelligentSearchService.getLocationSuggestions(input);
    
    if (data && data.predictions) {
      setPredictions(data.predictions);
      setShowDropdown(data.predictions.length > 0);
      setSelectedIndex(-1);
    } else if (error) {
      console.error('Error fetching predictions:', error);
      setPredictions([]);
      setShowDropdown(false);
    }
    
    setLoading(false);
  };

  const debouncedFetch = (input) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fetchPredictions(input);
    }, 300);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedFetch(value);
  };

  const handleInputFocus = () => {
    if (predictions.length > 0) {
      setShowDropdown(true);
    }
  };

  const handleInputBlur = (e) => {
    // Don't hide dropdown immediately if clicking on dropdown
    setTimeout(() => {
      setShowDropdown(false);
    }, 200);
  };

  const handlePredictionClick = (prediction) => {
    setInputValue(prediction.description);
    setShowDropdown(false);
    setSelectedIndex(-1);
    onSelect(prediction);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || predictions.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < predictions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : predictions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && predictions[selectedIndex]) {
          handlePredictionClick(predictions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const clearInput = () => {
    setInputValue('');
    setPredictions([]);
    setShowDropdown(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${className}`}
          autoComplete="off"
        />
        
        {/* Loading spinner or clear button */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          ) : inputValue ? (
            <button
              onClick={clearInput}
              className="text-gray-400 hover:text-gray-600"
              type="button"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          ) : (
            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          )}
        </div>
      </div>
      
      {showDropdown && predictions.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {predictions.map((prediction, index) => (
            <div
              key={prediction.place_id || index}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent input blur
                handlePredictionClick(prediction);
              }}
              className={`px-3 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                index === selectedIndex 
                  ? 'bg-blue-100 text-blue-900' 
                  : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-3 mt-1">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {prediction.structured_formatting?.main_text || prediction.description}
                  </div>
                  {prediction.structured_formatting?.secondary_text && (
                    <div className="text-xs text-gray-500 truncate mt-1">
                      {prediction.structured_formatting.secondary_text}
                    </div>
                  )}
                  {prediction.types && prediction.types.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {prediction.types.slice(0, 2).map((type, typeIndex) => (
                        <span 
                          key={typeIndex}
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
                        >
                          {type.replace(/_/g, ' ')}
                        </span>
                      ))}
                      {prediction.types.length > 2 && (
                        <span className="text-xs text-gray-400">
                          +{prediction.types.length - 2} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;